import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule, HttpHeaders } from '@angular/common/http';
import { getCookie } from '../../../../../utils/getCookies';
import { environment } from '../../../../../../environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { OrderKeyValueByDatePipe } from '../../../../../shared/pipes/order-keyvalue-by-date.pipe';
import { SocketService } from '../../../../../services/socket.service';
import { TranslateModule } from '@ngx-translate/core';
import { ModalWithResponseComponent } from '../../../../user/modals/modal-with-response/modal-with-response.component';
import { MatDialog } from '@angular/material/dialog';
import { ModalGeneralComponent } from '../../../../user/modals/modal-general/modal-general.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSelectModule } from '@angular/material/select';
import { commonEuropeanLanguages } from '../../../../../utils/languages';
import { MatChipEditedEvent, MatChipsModule } from '@angular/material/chips';
import {MatChipInputEvent} from '@angular/material/chips';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
  selector: 'app-job-offer-library',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, HttpClientModule, MatButtonModule, OrderKeyValueByDatePipe, TranslateModule, MatSelectModule, MatChipsModule, MatFormFieldModule],
  templateUrl: './job-offer-library.component.html',
  styleUrl: './job-offer-library.component.scss'
})
export class JobOfferLibraryComponent {

  // --- Outputs ---
  @Output() closeJobOfferLibraryEvent = new EventEmitter<void>();
  @Output() listJobOffersEvent = new EventEmitter<any[]>();

  // --- Estados generales de la vista ---
  /** Last file picked from the upload input. */
  selectedFile: File | null = null;
  /** Global loading flag for actions like upload/delete. */
  loading = false;
  /** Loading flag specific to manual job-offer creation. */
  loadingManualJobOffer = false;
  /** Controls whether the manual creation form is visible. */
  addingManual = false;

  // --- Formularios ---
  jobOfferForm: FormGroup;
  manualJobOfferForm: FormGroup;

  // --- Datos persistidos en memoria ---
  jobOffers: { [key: string]: any[] } = {};
  id_analysis: any = '';
  /**
   * Keeps the original, unfiltered payload so search can be cleared without re-fetching.
   */
  originalJobOffers: { [key: string]: any[] } = {};
  pushonlyJobOffers: boolean = false;

  languages: any[] = commonEuropeanLanguages;
  selected_idioms: any[] = [];


  constructor(private http: HttpClient, private formb: FormBuilder, private socketService: SocketService, private dialog: MatDialog, private route: ActivatedRoute, private router: Router) {
    this.jobOfferForm = this.formb.group({
      file: ['', Validators.required],
    });
    this.manualJobOfferForm = this.formb.group({
      id_jobOffer: [''],
      title: ['', Validators.required],
      job_description: ['', Validators.required],
      skills_tools: [''],
      languages: [[]],
      years_of_experience_required: [''],
      location: [''],
      idioms: [''],
      idiomslevel: [''],
      idioms_mandatory: [''],
    });

    this.detectifOnlyPushJobOffers();

    // Initial fetch of available job offers
    this.getFiles();

    // Listen for job offer preprocessing
    this.socketService.onJobOfferPreprocessing((data: any) => {

      if (typeof data.languages === 'string') {
        data.languages = JSON.parse(JSON.stringify(data.languages)) || [];
      }

      if (typeof data.skills_tools === 'string') {
        data.skills_tools = JSON.parse(JSON.stringify(data.skills_tools)) || [];
      }


      this.manualJobOfferForm.patchValue({
        title: data.title,
        job_description: data.job_description,
        skills_tools: data.skills_tools,
        languages: data.languages,
        years_of_experience_required: data.years_of_experience_required,
        location: data.location,
      });
      this.selected_idioms = data.languages;
    });

    // Listen for job offer preprocessing completed
    this.socketService.onJobOfferPreprocessingCompleted((data: any) => {
      this.loadingOffer = false;
      this.manualJobOfferForm.patchValue({
        id_jobOffer: data.id_jobOffer,
      });
    });

    // get the id_analysis from the query params
    this.route.queryParams.subscribe((params: any) => {
      if (params["id_analysis"] || localStorage.getItem('conectio_client_id_analysis')) {
        this.id_analysis = params["id_analysis"] || localStorage.getItem('conectio_client_id_analysis') || '';
        if (!this.pushonlyJobOffers) {
          this.getSelectedJobOffersFromAnalysis();
        }
      }
      else {
        this.router.navigate(['/dashboard/company/cv-deep-analyzer']);
      }
    });
  }

  detectifOnlyPushJobOffers() {
    if (this.router.url.endsWith('/dashboard/company/cv-deep-analyzer')) {
      this.pushonlyJobOffers = true;
      return;
    }
  }

  originalSelectedJobOffer: any[] = [];
  getSelectedJobOffersFromAnalysis() {


    const accessToken = getCookie('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });
    this.http.get(`${environment.url}/api/v1/CVDeepAnalyzer/analyzer/get_analysis_data`, { headers, params: { id_analysis: this.id_analysis } }).subscribe((res: any) => {
    this.selectedJobOffer = res.jobOffers;
    this.originalSelectedJobOffer = [...res.jobOffers];
    this.listJobOffersEvent.emit(this.originalSelectedJobOffer);
    console.log(this.originalSelectedJobOffer);
  }, (error: any) => {
    console.error('Error getting selected job offers', error);
  });
  }
  /**
   * Genera los headers necesarios para autenticación.
   */
  private buildAuthHeaders(): HttpHeaders {
    const accessToken = getCookie('access_token');
    return new HttpHeaders({ 'Authorization': `Bearer ${accessToken}` });
  }

  loadingFiles = false;
  /**
   * Retrieve job offers grouped by date from the backend.
   */
  getFiles() {
    this.loadingFiles = true;
    const headers = this.buildAuthHeaders();
    this.jobOffers = {};
    this.originalJobOffers = {};

    this.http.get(`${environment.url}/api/v1/JobOfferAnalyzer/JobOffer/get_files`, { headers }).subscribe((res: any) => {
      this.jobOffers = res.files;
      this.originalJobOffers = res.files;
      this.loadingFiles = false;
    }, (error: any) => {
      this.loadingFiles = false;
      console.error('Error getting files', error);
    });
  }

  /**
   * Handle file picker change and automatically start upload.
   */
  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.selectedFile = file;
      this.addFile();
    }
  }

  loadingOffer = false
  /**
   * Uploads the selected job offer file and triggers server-side preprocessing.
   */
  addFile() {
    this.loading = true;
    const headers = this.buildAuthHeaders();
    const formData: FormData = new FormData();
    formData.append('file', this.selectedFile!, this.selectedFile!.name);

    this.http.post(`${environment.url}/api/v1/JobOfferAnalyzer/JobOffer/save_file`, formData, { headers }).subscribe((res: any) => {
      console.log(res);
      this.jobOfferForm.reset();
      this.selectedFile = null;
      this.loading = false;

      const id_jobOffer = res.id_jobOffer;
      this.addingManual = true;
      this.loadingOffer = true;
      
      // once we know is the file saved, we emit to preprocess the file
      this.socketService.SendJobOfferToPreprocess({ id_jobOffer: id_jobOffer });


    }, (error: any) => {
      this.loading = false;
      console.error('Error uploading file', error);

      // we check if is for a type error
      if (error.error.error === 'files_wrong_extensions') {
        // we open the modal with the error
        ModalWithResponseComponent.open(this.dialog, {
          title: 'CVDeepAnalyzer.jobOfferLibrary.filesWrongExtensionsTitle',
          message: 'CVDeepAnalyzer.jobOfferLibrary.filesWrongExtensionsMessage',
          buttonText: 'CVDeepAnalyzer.jobOfferLibrary.filesWrongExtensionsButton',
          buttonTextCancel: 'CVDeepAnalyzer.jobOfferLibrary.filesWrongExtensionsButtonCancel',
          type: 'error'
        });
      } 
      else {
        // we open the modal with the error
        ModalWithResponseComponent.open(this.dialog, {
          title: 'CVDeepAnalyzer.errors.' + error.error.error + '.title',
          message: 'CVDeepAnalyzer.errors.' + error.error.error + '.message',
          buttonText: 'CVDeepAnalyzer.errors.' + error.error.error + '.buttonText',
          buttonTextCancel: 'CVDeepAnalyzer.errors.' + error.error.error + '.buttonTextCancel',
          type: 'error'
        });
      }
    });
  }

  /**
   * Delete a file by id and refresh the list afterwards.
   */
  deleteFile(id_job_offer: string) {
    const headers = this.buildAuthHeaders();
    this.loading = true;
    this.http.delete(`${environment.url}/api/v1/JobOfferAnalyzer/JobOffer/delete_file`, { headers, params: { id_file: id_job_offer } }).subscribe((res: any) => {
      this.getFiles();
      this.loading = false;
    }, (error: any) => {
      this.loading = false;
      console.error('Error deleting file', error);
    });
    
  }

  /**
   * Lightweight in-memory filter that searches within title or original filename.
   */
  filterJobOffers(event: Event) {
    const search = (event.target as HTMLInputElement).value;

    if (search.length > 0) {
      // Create a new object to avoid mutating originalFolders
      const filteredFolders: any = {};
      Object.keys(this.originalJobOffers).forEach((key) => {
        const filtered = this.originalJobOffers[key].filter((folder: any) =>
          folder?.preprocessed_json?.title?.toLowerCase().includes(search.toLowerCase()) || folder?.file_name?.toLowerCase().includes(search.toLowerCase())
        );
        filteredFolders[key] = filtered;
      });
      this.jobOffers = filteredFolders;
    } else {
      this.jobOffers = this.originalJobOffers;
    }
  }

  /**
   * Envía la información del formulario manual al backend.
   */
  saveJobOffer() {
    this.loadingManualJobOffer = true;
    const headers = this.buildAuthHeaders();

    this.http.post(`${environment.url}/api/v1/JobOfferAnalyzer/JobOffer/saveJobOffer`, this.manualJobOfferForm.value, { headers }).subscribe((res: any) => {
      console.log(res);
      this.loadingManualJobOffer = false;
      this.toggleJobOffer();
    }, (error: any) => {
      this.loadingManualJobOffer = false;
      console.error('Error saving job offer', error);
    });
  }

  selectedJobOffer: any[] = [];
  loading_selected_job_offer = false;
  /**
   * Emit selected job-offer IDs to parent so it can proceed with comparisons.
   */
  savingSelectedJobOffer = false;
  setSelectedJobOffer() {
    this.savingSelectedJobOffer = true;
    const accessToken = getCookie('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });
    this.http.post(`${environment.url}/api/v1/CVDeepAnalyzer/analyzer/update_analysis`, { id_analysis: this.id_analysis, id_job_offers: this.selectedJobOffer, only_job_offers: 1}, { headers }).subscribe((res: any) => {
      this.savingSelectedJobOffer = false;
      this.closeJobOfferLibrary();

      this.listJobOffersEvent.emit(this.selectedJobOffer);


      this.router.navigate(['/dashboard/company/cv-deep-analyzer/analyzer'], { queryParams: { id_analysis: this.id_analysis } });


    }, (error: any) => {
      this.savingSelectedJobOffer = false;
      console.error('Error setting selected CVs', error);
    });
  }

  /**
   * Toggle selection state for a job-offer card.
   */
  PushSelectedJobOffer(jobOffersId: any) {
    if (this.pushonlyJobOffers) {
      return;
    }

    this.selectedJobOffer = []
    this.selectedJobOffer.push(jobOffersId);

  }

  /**
   * Close the floating library UI.
   */
  closeJobOfferLibrary() {
    this.closeJobOfferLibraryEvent.emit();
  }


  /**
   * Switch between listing job offers and displaying the manual creation form.
   */
  toggleJobOffer() {
    this.addingManual = !this.addingManual;
    this.manualJobOfferForm.reset();
    this.manualJobOfferForm.enable();

    if (!this.addingManual) {
      this.getFiles();
      this.seeOfferdetected = false;

    }
  }


  /**
   * Loads job-offer data into the manual form so the user can edit it.
   */
  editJobOffer(jobOfferId: string) {
    this.addingManual = true;

    const jobOffer = Object.values(this.jobOffers).flat().find((jobOffer: any) => jobOffer.id === jobOfferId);
    
    
    if (typeof jobOffer?.preprocessed_json?.skills_tools === 'string') {
      jobOffer.preprocessed_json.skills_tools = JSON.parse(JSON.stringify(jobOffer?.preprocessed_json?.skills_tools)) || [];
    }
    
    if (typeof jobOffer?.preprocessed_json?.languages === 'string') {
      jobOffer.preprocessed_json.languages = JSON.parse(JSON.stringify(jobOffer?.preprocessed_json?.languages)) || [];
    }
    
    this.manualJobOfferForm.patchValue({
      id_jobOffer: jobOffer?.id,
      title: jobOffer?.preprocessed_json?.title,
      job_description: jobOffer?.preprocessed_json?.job_description,
      skills_tools: jobOffer?.preprocessed_json?.skills_tools,
      languages: jobOffer?.preprocessed_json?.languages,
      years_of_experience_required: jobOffer?.preprocessed_json?.years_of_experience_required,
      location: jobOffer?.preprocessed_json?.location,
    });
    this.selected_idioms = JSON.parse(JSON.stringify(jobOffer?.preprocessed_json?.languages)) || [];
  }

  /**
   * Shows the job-offer details in read-only mode.
   */
  seeOfferdetected: boolean = false;
  seeOffer(jobOfferId: string) {
    this.editJobOffer(jobOfferId);

    this.seeOfferdetected = true;

    // disable all form controls
    this.manualJobOfferForm.disable();
  }

  /**
   * Deletes a job offer and refreshes the list.
   */
  deleteJobOffer(jobOfferId: string) {
    // we open the confirm modal
    ModalWithResponseComponent.open(this.dialog, {
      title: 'CVDeepAnalyzer.jobOfferLibrary.deleteJobOfferTitle',
      message: 'CVDeepAnalyzer.jobOfferLibrary.deleteJobOfferMessage',
      buttonText: 'CVDeepAnalyzer.jobOfferLibrary.deleteJobOfferButton',
      buttonTextCancel: 'CVDeepAnalyzer.jobOfferLibrary.deleteJobOfferButtonCancel',
    }).afterClosed().subscribe((result: any) => {
      if (result) {
        const headers = this.buildAuthHeaders();
        this.http.post(`${environment.url}/api/v1/JobOfferAnalyzer/JobOffer/delete_file`, { id_file: jobOfferId }, { headers }).subscribe((res: any) => {
          this.getFiles();
        }, (error: any) => {
          console.error('Error deleting job offer', error);
        });
      }
    });
  }

  isNotTheSameArray(array1: any[], array2: any[]) {
    return array1.length !== array2.length || !array1.every((value, index) => value === array2[index]);
  }


  addidiom() {

    if (!this.manualJobOfferForm.get('idioms')?.value || !this.manualJobOfferForm.get('idiomslevel')?.value || !this.manualJobOfferForm.get('idioms_mandatory')?.value) {
      return;
    }

    this.selected_idioms.push({
      language: this.manualJobOfferForm.get('idioms')?.value,
      level: this.manualJobOfferForm.get('idiomslevel')?.value,
      mandatory: this.manualJobOfferForm.get('idioms_mandatory')?.value,
    });
    this.manualJobOfferForm.get('languages')?.setValue(this.selected_idioms);
  }

  deleteidiom(idiom: any) {
    this.selected_idioms = this.selected_idioms.filter((item) => item !== idiom);
    this.manualJobOfferForm.get('languages')?.setValue(this.selected_idioms);
  }

  get_lang(code: string) {
    return this.languages.find((language: any) => language.code === code)?.name || code;
  }

  // --- Chips ---

  selectable = true;
  removable = true;
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  add(event: any): void {
    const value = (event.value || '').trim();

    // we add the skill to the manualJobOfferForm


    // Add our fruit
    if (value) {
      this.manualJobOfferForm.get('skills_tools')?.setValue([...this.manualJobOfferForm.get('skills_tools')?.value, value]);
    }

    // Clear the input value
    event.chipInput!.clear();
  }

  remove(skill: any): void {
    const index = this.manualJobOfferForm.get('skills_tools')?.value?.indexOf(skill);

    if (index >= 0) {
      const newSkills = this.manualJobOfferForm.get('skills_tools')?.value.filter((skill2: any) => skill2 !== skill);
      this.manualJobOfferForm.get('skills_tools')?.setValue(newSkills);
    }
  }
}
